import React, { useState, useEffect } from 'react';
import Header from '../../../Components/Header';
import Sidebar from '../../../Components/Sidebar';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAllDocuments, addDocument, updateDocument, deleteDocument } from '../../../firebase/dbService';
import { uploadFile, getFileURL, deleteFileFromStorage } from '../../../firebase/storageService';
import ImageUpload from '../../../Components/ImageUpload';

const ContactUsBackground = () => {
  const [contactUsData, setContactUsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Contact us',
    subtitle: "Let's Connect",
    backgroundImage: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageURL, setImageURL] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchContactUsData();
  }, []);

  const fetchContactUsData = async () => {
    try {
      const snapshot = await getAllDocuments('contactUsBackground');
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        const docId = snapshot.docs[0].id;
        
        // Get image URL if exists
        let imageUrl = '';
        if (data.backgroundImage) {
          try {
            imageUrl = await getFileURL(data.backgroundImage);
          } catch (error) {
            console.error('Error loading image:', error);
          }
        }
        
        setContactUsData({ id: docId, ...data, backgroundImageUrl: imageUrl });
        setFormData({
          title: data.title || 'Contact us',
          subtitle: data.subtitle || "Let's Connect",
          backgroundImage: data.backgroundImage || ''
        });
        setImageURL(imageUrl);
        setIsEditing(true);
      } else {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error fetching contact us data:', error);
      toast.error('Error loading data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageLoad = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newImageURL = URL.createObjectURL(file);
      setImageFile(file);
      setImageURL(newImageURL);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let backgroundImagePath = formData.backgroundImage;

      // Upload new image if provided
      if (imageFile) {
        try {
          // Delete old image if updating
          if (isEditing && contactUsData && contactUsData.backgroundImage) {
            try {
              await deleteFileFromStorage(contactUsData.backgroundImage);
              console.log('Old image deleted:', contactUsData.backgroundImage);
            } catch (deleteError) {
              console.warn('Could not delete old image:', deleteError);
              // Continue with upload even if delete fails
            }
          }

          // Create unique filename with timestamp
          const timestamp = Date.now();
          const fileExtension = imageFile.name.split('.').pop();
          const uniqueFilename = `contact-us-bg-${timestamp}.${fileExtension}`;
          
          backgroundImagePath = await uploadFile(
            imageFile,
            `page-backgrounds/contact-us/${uniqueFilename}`
          );
          console.log('Image uploaded to path:', backgroundImagePath);
        } catch (error) {
          console.error('Error uploading image:', error);
          toast.error('Error uploading image');
          setLoading(false);
          return;
        }
      }

      const dataToSave = {
        title: formData.title,
        subtitle: formData.subtitle,
        backgroundImage: backgroundImagePath,
        updatedAt: new Date()
      };

      if (isEditing && contactUsData) {
        await updateDocument('contactUsBackground', contactUsData.id, dataToSave);
        toast.success('Contact Us background updated successfully!');
      } else {
        await addDocument('contactUsBackground', dataToSave);
        toast.success('Contact Us background created successfully!');
      }

      fetchContactUsData();
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error('Error saving data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Sidebar
        id="content-menu"
        id1="content-items"
        activeClassName="contactus-background"
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/herocarousel">Website Content</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Contact Us Background</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>{isEditing ? 'Edit Contact Us Background' : 'Add Contact Us Background'}</h4>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>Page Title <span className="login-danger">*</span></label>
                          <input
                            className="form-control"
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>Subtitle</label>
                          <input
                            className="form-control"
                            type="text"
                            name="subtitle"
                            value={formData.subtitle}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="form-group local-forms">
                          <label>Background Image</label>
                          <ImageUpload
                            id="contactus-bg"
                            src={imageURL}
                            loadFile={handleImageLoad}
                            imageName="Background Image"
                          />
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="form-action-group text-end">
                          <button
                            type="submit"
                            className="btn btn-primary submit-form me-2"
                            disabled={loading}
                          >
                            {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary cancel-form"
                            onClick={() => window.history.back()}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Preview Section */}
              {contactUsData && (
                <div className="card mt-4">
                  <div className="card-body">
                    <h5 className="card-title">Preview</h5>
                    <div 
                      className="preview-container position-relative"
                      style={{
                        height: '300px',
                        backgroundImage: `url(${contactUsData.backgroundImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '8px'
                      }}
                    >
                      <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-center align-items-center text-white"
                           style={{ background: 'rgba(0, 0, 0, 0.5)' }}>
                        <p className="mb-1">{contactUsData.subtitle}</p>
                        <h3 className="text-white mb-0">{contactUsData.title}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default ContactUsBackground;